import { apiClient } from '../lib/apiClient';

/**
 * App feedback (backend src/routes/meFeedback.routes.ts, src/routes/adminFeedback.routes.ts):
 *   POST      /api/v1/me/feedback  { liked?, problems?, missing?, source, locale } → 201
 *   GET       /api/v1/me/feedback/prompt               → { data: { show, optOut } }
 *   PUT       /api/v1/me/feedback/prompt { optOut }    → { data: { optOut } }
 *   POST      /api/v1/me/feedback/prompt/shown         → 204
 *   GET       /api/v1/admin/feedback?limit&offset      → { data: AdminFeedbackItem[], meta: { total } }
 *
 * `show` is true on every 10th login until the dialog was opened for it, unless
 * the user opted out ("Nicht mehr automatisch anzeigen").
 */
export type FeedbackSource = 'menu' | 'prompt';

export interface FeedbackInput {
  liked: string;
  problems: string;
  missing: string;
  source: FeedbackSource;
  locale?: string;
}

export interface FeedbackPromptState {
  show: boolean;
  optOut: boolean;
}

export interface AdminFeedbackItem {
  id: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  liked: string | null;
  problems: string | null;
  missing: string | null;
  source: FeedbackSource;
  locale: string | null;
  createdAt: string;
}

export interface AdminFeedbackPage {
  items: AdminFeedbackItem[];
  total: number;
}

/** "de-DE" → "de"; anything the API would refuse is left out. */
function toLocale(value: string | undefined): string | undefined {
  const base = value?.split(/[-_]/)[0]?.toLowerCase();
  return base && /^[a-z]{2,3}$/.test(base) ? base : undefined;
}

export async function submitFeedback(input: FeedbackInput): Promise<void> {
  await apiClient.post('/api/v1/me/feedback', {
    liked: input.liked,
    problems: input.problems,
    missing: input.missing,
    source: input.source,
    locale: toLocale(input.locale),
  });
}

export async function fetchFeedbackPrompt(): Promise<FeedbackPromptState> {
  const body = await apiClient.get<unknown>('/api/v1/me/feedback/prompt');
  const data = (body as { data?: { show?: unknown; optOut?: unknown } } | null)?.data;
  return { show: data?.show === true, optOut: data?.optOut === true };
}

export async function markFeedbackPromptShown(): Promise<void> {
  await apiClient.post('/api/v1/me/feedback/prompt/shown', {});
}

export async function updateFeedbackPromptOptOut(optOut: boolean): Promise<boolean> {
  const body = await apiClient.put<unknown>('/api/v1/me/feedback/prompt', { optOut });
  const data = (body as { data?: { optOut?: unknown } } | null)?.data;
  return typeof data?.optOut === 'boolean' ? data.optOut : optOut;
}

export async function fetchAdminFeedback(limit: number, offset: number): Promise<AdminFeedbackPage> {
  const res = await apiClient.get<{ data: AdminFeedbackItem[]; meta?: { total?: number } }>(
    `/api/v1/admin/feedback?limit=${limit}&offset=${offset}`,
  );
  return { items: res.data ?? [], total: res.meta?.total ?? 0 };
}
